import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation } from 'react-router-dom';

import { getEmployee } from '../../services/employeeService';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import EmployeeGeneralTab from './tab-pages/EmployeeGeneralTab';
import PersonalInformationTab from './tab-pages/PersonalInformationTab';
import CareerHistoryTab from './tab-pages/CareerHistoryTab';
import EmployeeApprovalFlowsTab from './tab-pages/EmployeeApprovalFlowsTab';
import EmployeeAccessPermissionsTab from './tab-pages/EmployeeAccessPermissionsTab';
import EmployeeScheduleTab from './tab-pages/EmployeeScheduleTab';
import EmployeeCertificationsTab from './tab-pages/EmployeeCertificationsTab';
import EmployeeResumeTab from './tab-pages/EmployeeResumeTab';
import EmployeePerformanceTab from './tab-pages/EmployeePerformanceTab';
import EmployeePersonnelFileTab from './tab-pages/EmployeePersonnelFileTab';
import EmployeeChangeHistoryTab from './tab-pages/EmployeeChangeHistoryTab';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StickyHeader = styled.div`
  flex-shrink: 0;
`;

const TabContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
`;

export default function EmployeeDetailsPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const { canWrite, canRead } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    getEmployee(id)
      .then((res) => setEmployee(res.data?.data))
      .catch(() => {});
  }, [id]);

  const employeeName = location.state?.user
    ? `${location.state.user.firstName} ${location.state.user.lastName}`
    : employee
      ? `${employee.firstName} ${employee.lastName}`
      : '...';

  const hasWrite = canWrite('EM');
  // P1-C — Performans sekmesi PM.Read gerektirir (EM yetkisinden bağımsız).
  const canSeePerformance = canRead('PM');

  const tabs = useMemo(() => {
    const all = [
      { key: 'general', label: t('employee.general') },
      { key: 'personal', label: t('employee.personalInfo'), writeOnly: true },
      { key: 'career', label: t('employee.careerHistory') },
      { key: 'personnelFile', label: t('employee.personnelFile'), writeOnly: true },
      ...(canSeePerformance ? [{ key: 'performance', label: t('employee.performanceTab') }] : []),
      { key: 'approvalFlows', label: t('employee.approvalFlows'), writeOnly: true },
      { key: 'accessPermissions', label: t('employee.accessPermissions'), writeOnly: true },
      { key: 'schedule', label: t('employee.schedule'), writeOnly: true },
      { key: 'certifications', label: t('employee.certifications'), writeOnly: true },
      { key: 'resume', label: t('employee.resume') },
      // AKM POC §9.1 — kişinin veri ve yetki değişiklikleri (işlem geçmişinden; İK).
      { key: 'changeHistory', label: t('employee.changeHistory'), writeOnly: true },
    ];
    return hasWrite ? all : all.filter(tab => !tab.writeOnly);
  }, [t, hasWrite, canSeePerformance]);

  return (
    <PageContainer>
      <StickyHeader>
        <Breadcrumbs items={[
          { label: t('employee.title'), path: '/employees' },
          { label: employeeName },
        ]} />
        <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </StickyHeader>

      <TabContent>
        {activeTab === 'general' && <EmployeeGeneralTab employeeId={id} />}
        {activeTab === 'personal' && <PersonalInformationTab employeeId={id} />}
        {activeTab === 'career' && <CareerHistoryTab employeeId={id} />}
        {activeTab === 'personnelFile' && <EmployeePersonnelFileTab employeeId={id} />}
        {activeTab === 'performance' && <EmployeePerformanceTab employeeId={id} />}
        {activeTab === 'approvalFlows' && <EmployeeApprovalFlowsTab employeeId={id} />}
        {activeTab === 'accessPermissions' && <EmployeeAccessPermissionsTab employeeId={id} />}
        {activeTab === 'schedule' && <EmployeeScheduleTab employeeId={id} />}
        {activeTab === 'certifications' && <EmployeeCertificationsTab employeeId={id} />}
        {activeTab === 'resume' && <EmployeeResumeTab employeeId={id} />}
        {activeTab === 'changeHistory' && <EmployeeChangeHistoryTab employeeId={id} />}
      </TabContent>
    </PageContainer>
  );
}
